import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api, publicUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { VolunteerFilters } from "@/components/VolunteerFilters";

export default function Volunteers() {
  const { church } = useAuth();
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => Object.fromEntries(params.entries()), [params]);
  const pro = church?.billing?.pro;

  useEffect(() => { api.get("/volunteers", { params: filters }).then((r) => setList(r.data)); }, [filters]);

  const filtered = useMemo(() => (list || []).filter((v) =>
    `${v.name} ${v.email} ${v.matches?.[0]?.name}`.toLowerCase().includes(q.toLowerCase())), [list, q]);

  return (
    <div className="fade-up">
      <p className="eyebrow">Your people</p>
      <div className="flex flex-wrap items-end justify-between gap-4 mt-2">
        <h1 className="font-display text-navy text-4xl font-semibold tracking-tight">Volunteers</h1>
        <div className="relative w-full sm:w-72">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-ink" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, team"
            className="pl-9 h-11 rounded-lg bg-white" data-testid="volunteer-search-input" />
        </div>
      </div>

      {pro && <VolunteerFilters filters={filters} setFilters={(f) => setParams(Object.fromEntries(Object.entries(f).filter(([, v]) => v)))} />}
      {list && <p className="text-sm text-muted-ink mt-4" data-testid="volunteer-count">{filtered.length} volunteer{filtered.length === 1 ? "" : "s"}</p>}
      <div className="card-soft mt-3 overflow-hidden">
        {list && filtered.length === 0 ? (
          <div className="p-12 text-center" data-testid="volunteers-empty">
            <p className="font-display text-navy text-xl">{Object.keys(filters).length ? "No volunteers match these filters." : "No assessments yet."}</p>
            {!Object.keys(filters).length && <p className="text-muted-ink mt-2">Share <span className="font-mono-dt text-navy">{publicUrl(church.slug)}</span> to get started.</p>}
          </div>
        ) : (
          <table className="w-full text-left">
            <thead className="bg-paper text-xs uppercase tracking-[0.12em] text-muted-ink">
              <tr><th className="px-6 py-4">Name</th><th className="px-6 py-4 hidden md:table-cell">DISC</th>
                <th className="px-6 py-4 hidden md:table-cell">Top gifts</th><th className="px-6 py-4">Best fit</th>{pro && <th className="px-6 py-4 hidden lg:table-cell">Onboarding</th>}
                <th className="px-6 py-4 hidden sm:table-cell">Date</th></tr>
            </thead>
            <tbody className="divide-y divide-[var(--line)]">
              {filtered.map((v) => (
                <tr key={v.volunteer_id} className="hover:bg-paper/60 transition-colors" data-testid={`volunteer-row-${v.volunteer_id}`}>
                  <td className="px-6 py-4">
                    <Link to={`/volunteers/${v.volunteer_id}`} className="font-medium text-navy hover:text-teal underline decoration-dotted underline-offset-4" data-testid={`volunteer-link-${v.volunteer_id}`}>{v.name}</Link>
                    <p className="text-xs text-muted-ink">{v.email}</p>
                  </td>
                  <td className="px-6 py-4 hidden md:table-cell font-display font-semibold text-teal">{v.disc_profile}</td>
                  <td className="px-6 py-4 hidden md:table-cell text-sm text-muted-ink">{v.top_gifts?.join(", ")}</td>
                  <td className="px-6 py-4 text-sm"><span className="bg-teal-soft text-teal rounded-full px-3 py-1">{v.matches?.[0]?.name}</span></td>
                  {pro && <td className="px-6 py-4 hidden lg:table-cell text-sm text-muted-ink" data-testid={`volunteer-onboarding-${v.volunteer_id}`}>{v.onboarding?.status}{v.approval ? ` · ✓ ${v.approval.name}` : ""}</td>}
                  <td className="px-6 py-4 hidden sm:table-cell text-sm text-muted-ink">{new Date(v.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
